import { useMemo } from 'react';

import {
  type FilterDefinition,
  ToggleFilterChip,
  createToggleFilter,
} from '@spotify/confidence-core-react';

import { useCurrentUserAndGroupsFilter } from '../useCurrentUserAndGroupsFilter';

export const OWNED_BY_ME_FILTER_ID = 'ownedByMe';

const UNAVAILABLE_OWNER_PREDICATE =
  'owner:"identities/owned-by-me-unavailable"';
const UNAVAILABLE_OWNER_FILTER = `${UNAVAILABLE_OWNER_PREDICATE} AND NOT ${UNAVAILABLE_OWNER_PREDICATE}`;

export const useOwnedByMeFilter = ({
  skip = false,
}: { skip?: boolean } = {}): FilterDefinition => {
  const { filter, isLoading, error } = useCurrentUserAndGroupsFilter({
    field: 'owner',
    skip,
  });
  const unavailable = skip || isLoading || Boolean(error) || !filter;
  let label = 'Owned by me';
  let description = 'Resources owned by you or a group you belong to';
  if (!skip && error) {
    label = 'Owned by me (failed to load)';
    description =
      'Could not load your ownership identities. Try reloading the page.';
  } else if (!skip && isLoading) {
    label = 'Owned by me (loading)';
    description = 'Loading your ownership identities';
  } else if (unavailable) {
    label = 'Owned by me (unavailable)';
    description = 'Your ownership identities are unavailable';
  }

  return useMemo(
    () =>
      createToggleFilter({
        id: OWNED_BY_ME_FILTER_ID,
        label,
        description,
        query: () =>
          unavailable
            ? UNAVAILABLE_OWNER_FILTER
            : (filter ?? UNAVAILABLE_OWNER_FILTER),
        viewer: ToggleFilterChip,
      }),
    [filter, unavailable, label, description],
  );
};
